"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import config from "../config";
import { activityService } from "../services/activityService";
import mockDb from "../services/mockDbInstance";
import { MockDatabase } from '@/services/MockDatabaseClass';
import { Device, DeviceState } from "@/types";
import { ActivityTracker } from '@/utils/activityTracker';

// Use the config object which has dynamic URL
const WS_URL = config.ws?.url || "ws://localhost:5000";
const DEMO_MODE = process.env.NEXT_PUBLIC_DEMO_MODE === "true";

interface WebSocketContextType {
  socket: WebSocket | null;
  isConnected: boolean;
  error: Error | null;
  lastMessage: any;
  energyData: Record<string, any>;
  sendMessage: (message: any) => void;
  reconnect: () => void;
  requestGroupRead: () => void;
  requestAreaGroupRead: (areaId: string) => void;
  subscribeToAllDevices: (devices: any[]) => void;
  subscribeToControlValues: (deviceIds: string[]) => void;
  subscribeToAreaWithControlValues: (areaId: string) => void;
  subscribeToEnergyUpdates: (deviceIds: string[]) => void;
  devices: Device[];
}

const defaultContextValue: WebSocketContextType = {
  socket: null,
  isConnected: false,
  error: null,
  lastMessage: null,
  energyData: {},
  sendMessage: () => { },
  reconnect: () => { },
  requestGroupRead: () => { },
  requestAreaGroupRead: () => { },
  subscribeToAllDevices: () => { },
  subscribeToControlValues: () => { },
  subscribeToAreaWithControlValues: () => { },
  subscribeToEnergyUpdates: () => { },
  devices: [],
};

const WebSocketContext = createContext<WebSocketContextType>(defaultContextValue);

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(DEMO_MODE);
  const [error, setError] = useState<Error | null>(null);
  const [lastMessage, setLastMessage] = useState<any>(null);
  const [energyData, setEnergyData] = useState<Record<string, any>>({});
  const [devices, setDevices] = useState<Device[]>([]);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const messageQueueRef = useRef<any[]>([]);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 3;
  const reconnectDelay = 3000;
  const socketRef = useRef<WebSocket | null>(null);
  const isConnectingRef = useRef(false);
  const mockDb = MockDatabase.getInstance();

  // Subscribe to mock database updates in demo mode
  useEffect(() => {
    if (DEMO_MODE) {
      // Simulate WebSocket connection
      setIsConnected(true);
      setDevices(mockDb.getDevices());

      // Subscribe to device state changes
      const handleDeviceUpdate = (deviceId: string, state: DeviceState) => {
        setDevices(mockDb.getDevices());
      };

      mockDb.subscribe("websocket-context", handleDeviceUpdate);

      return () => {
        setIsConnected(false);
        mockDb.unsubscribe("websocket-context");
      };
    }
  }, []);

  const connect = useCallback(() => {
    if (DEMO_MODE) {
      // console.log("Running in demo mode, skipping WebSocket connection");
      return null;
    }

    // Prevent multiple connection attempts
    if (isConnectingRef.current) {
      // console.log("Connection attempt already in progress, skipping");
      return null;
    }

    try {
      isConnectingRef.current = true;

      // Close any existing socket
      if (socketRef.current) {
        // console.log("Closing existing socket connection");
        try {
          socketRef.current.close();
        } catch (err) {
          console.error("Error closing existing socket:", err);
        }
      }

      console.log("Connecting to WebSocket server at:", WS_URL);
      const ws = new WebSocket(WS_URL);
      socketRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;
        isConnectingRef.current = false;
        // console.log("✅ WebSocket connection established successfully");

        // Send any queued messages
        if (messageQueueRef.current.length > 0) {
          // console.log(
          //   `Sending ${messageQueueRef.current.length} queued messages`,
          // );
          messageQueueRef.current.forEach((msg) => {
            ws.send(JSON.stringify(msg));

            // Dispatch custom event for the command monitor
            const event = new CustomEvent("websocket-message-sent", {
              detail: msg,
            });
            window.dispatchEvent(event);
          });
          messageQueueRef.current = [];
        }

        // Subscribe to all device updates
        ws.send(
          JSON.stringify({
            type: "subscribe",
            subscriptionType: "all",
          }),
        );
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // console.log("Received WebSocket message:", data);

          // Handle different message types
          switch (data.type) {
            case "device_discovery":
              // Dispatch a custom event for device discovery
              const discoveryEvent = new CustomEvent("device-discovery", {
                detail: data.data,
              });
              window.dispatchEvent(discoveryEvent);
              break;

            case "deviceState":
              // Update device state
              setLastMessage(data);

              // Canonical UI event: pages listen to this for instant updates.
              // This avoids races where some pages only rely on lastMessage polling.
              if (typeof window !== "undefined" && data.deviceId && data.state) {
                try {
                  window.dispatchEvent(
                    new CustomEvent("device-state-update", {
                      detail: { deviceId: data.deviceId, state: data.state },
                    }),
                  );
                } catch (e) {
                  // ignore event dispatch errors
                }
              }

              // Track device state changes for activity logging
              if (data.deviceId && data.state) {
                const device = devices.find(d => d.id === data.deviceId);
                if (device) {
                  // Track power state changes
                  if (data.state.power !== undefined) {
                    if (data.state.power) {
                      ActivityTracker.trackDeviceTurnedOn(device.name, device.areaId || 'System').catch(console.error);
                    } else {
                      ActivityTracker.trackDeviceTurnedOff(device.name, device.areaId || 'System').catch(console.error);
                    }
                  }

                  // Track motion detection
                  if (data.state.motion !== undefined && data.state.motion) {
                    ActivityTracker.trackMotionDetected(device.areaId || 'System', device.name).catch(console.error);
                  }

                  // Track temperature changes
                  if (data.state.temperature !== undefined) {
                    ActivityTracker.trackActivity('climate', `Temperature reading: ${data.state.temperature}°C for ${device.name}`, device.areaId || 'System', { temperature: data.state.temperature }).catch(console.error);
                  }

                  // Track light level changes
                  if (data.state.lightLevel !== undefined) {
                    ActivityTracker.trackDaylightReading(device.areaId || 'System', data.state.lightLevel, device.name).catch(console.error);
                  }
                }
              }
              break;

            case "energyUpdate":
              // Update energy data
              setEnergyData((prev) => ({
                ...prev,
                [data.deviceId]: data.data,
              }));
              break;

            default:
              // Update last message for other types
              setLastMessage(data);
          }
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };

      ws.onerror = (event) => {
        // Only log in development mode to reduce console noise
        if (process.env.NODE_ENV === 'development') {
          console.warn("WebSocket connection failed - server may not be running");
        }
        setError(new Error("WebSocket connection error"));
        setIsConnected(false);
        isConnectingRef.current = false;
      };

      ws.onclose = (event) => {
        // console.log("WebSocket connection closed:", {
        //   code: event.code,
        //   reason: event.reason,
        //   wasClean: event.wasClean,
        //   readyState: ws.readyState
        // });
        setIsConnected(false);
        isConnectingRef.current = false;

        // Attempt to reconnect if not manually closed
        if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = reconnectDelay * Math.pow(2, reconnectAttemptsRef.current);
          // console.log(`Attempting to reconnect in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            connect();
          }, delay);
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          console.error("Max reconnection attempts reached. Please check if the WebSocket server is running.");
          setError(new Error("Max reconnection attempts reached. Please check if the WebSocket server is running."));
        }
      };

      setSocket(ws);
      return ws;
    } catch (error) {
      console.error("Error creating WebSocket connection:", error);
      setError(error instanceof Error ? error : new Error(String(error)));
      isConnectingRef.current = false;
      return null;
    }
  }, []);

  const reconnect = useCallback(() => {
    // Clear any existing reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    // Reset reconnect attempts when manually reconnecting
    reconnectAttemptsRef.current = 0;

    // Close existing socket if any
    if (socketRef.current) {
      try {
        socketRef.current.close(1000, "Manual reconnect");
      } catch (err) {
        console.error("Error closing socket during reconnect:", err);
      }
    }

    // Connect after a short delay to ensure the previous socket is closed
    setTimeout(() => {
      connect();
    }, 100);
  }, [connect]);

  const sendMessage = useCallback(
    (message: any) => {
      if (
        socketRef.current &&
        socketRef.current.readyState === WebSocket.OPEN
      ) {
        // console.log("Sending WebSocket message:", message);
        socketRef.current.send(JSON.stringify(message));

        // Dispatch custom event for the command monitor
        const event = new CustomEvent("websocket-message-sent", {
          detail: message,
        });
        window.dispatchEvent(event);
      } else {
        console.warn("WebSocket not ready, queueing message:", message);
        messageQueueRef.current.push(message);

        // Try to reconnect if not already connecting
        if (!isConnectingRef.current) {
          reconnect();
        }
      }
    },
    [reconnect],
  );

  const requestGroupRead = useCallback(() => {
    sendMessage({
      type: "READ_GROUP",
      payload: {
        groupType: "page",
      },
    });
  }, [sendMessage]);

  const requestAreaGroupRead = useCallback(
    (areaId: string) => {
      if (!areaId) return;

      // console.log(`Requesting device states for area: ${areaId}`);
      sendMessage({
        type: "READ_GROUP",
        payload: {
          groupType: `area:${areaId}`,
        },
      });
    },
    [sendMessage],
  );

  const subscribeToAllDevices = useCallback(
    (devices: any[]) => {
      if (!devices || devices.length === 0) return;

      const deviceIds = devices.map((device) => device.id);
      // console.log(`Subscribing to updates for ${deviceIds.length} devices`);

      sendMessage({
        type: "subscribe",
        deviceIds,
      });
    },
    [sendMessage],
  );

  const subscribeToControlValues = useCallback(
    (deviceIds: string[]) => {
      if (!deviceIds || deviceIds.length === 0) return;

      // console.log(
      //   `Subscribing to control values for ${deviceIds.length} devices`,
      // );

      sendMessage({
        type: "subscribe",
        deviceIds,
      });
    },
    [sendMessage],
  );

  const subscribeToAreaWithControlValues = useCallback(
    (areaId: string) => {
      if (!areaId) return;

      // console.log(`Subscribing to control values for area: ${areaId}`);

      // Use the 'subscribe' command type instead of 'SUBSCRIBE_AREA'
      // First fetch all devices in the area
      fetch(`/api/spaces/${areaId}/devices`)
        .then((response) => response.json())
        .then((data) => {
          if (data.success && Array.isArray(data.devices)) {
            const deviceIds = data.devices.map((device: any) => device.id);
            // console.log(
            //   `Subscribing to ${deviceIds.length} devices in area ${areaId}`,
            // );

            // Use the 'subscribe' command that the server supports
            sendMessage({
              type: "subscribe",
              deviceIds: deviceIds,
            });
          } else {
            console.error("Failed to get devices for area subscription:", data);
          }
        })
        .catch((error) => {
          console.error("Error fetching devices for area subscription:", error);
        });
    },
    [sendMessage],
  );

  // Subscribe to energy updates for specific devices
  const subscribeToEnergyUpdates = useCallback(
    (deviceIds: string[]) => {
      if (!deviceIds || !deviceIds.length) {
        console.warn("No device IDs provided to subscribe to energy updates");
        return;
      }

      const message = {
        action: "subscribe",
        topic: "energyUpdates",
        deviceIds,
      };

      sendMessage(message);
      // console.log(
      //   `Subscribed to energy updates for ${deviceIds.length} devices`,
      // );
    },
    [sendMessage],
  );

  // Initialize WebSocket connection
  useEffect(() => {
    if (!DEMO_MODE) {
      connect();
    }

    return () => {
      // Cleanup on unmount
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (socketRef.current) {
        try {
          socketRef.current.close(1000, "Component unmounting");
        } catch (err) {
          console.error("Error closing socket during cleanup:", err);
        }
      }
    };
  }, [connect]);

  const value = {
    socket,
    isConnected,
    error,
    lastMessage,
    energyData,
    sendMessage,
    reconnect,
    requestGroupRead,
    requestAreaGroupRead,
    subscribeToAllDevices,
    subscribeToControlValues,
    subscribeToAreaWithControlValues,
    subscribeToEnergyUpdates,
    devices,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

// Add an alias for backward compatibility
export const useWebSocketContext = useWebSocket;
